<template>
  <div class="pt-2" v-if="aviso">
    <!-- O fenômeno é o título do painel. O cabeçalho verde traz só "Aviso meteorológico"
         (ver abrirAvisoInmet), então é aqui que o usuário lê O QUE é o alerta. -->
    <h2 class="na-inmet-titulo">{{ aviso.descricao }}</h2>

    <!-- Severidade: barra fina na cor do INMET à esquerda, texto sobre fundo neutro.
         Preencher o card inteiro com a cor (amarelo puro, laranja) domina o painel sem
         acrescentar informação — a cor identifica, não precisa gritar. -->
    <div class="na-section na-inmet-sev">
      <span class="na-inmet-sev-barra" :style="{ backgroundColor: aviso.cor }"></span>
      <div class="na-inmet-sev-texto">
        <span class="na-inmet-sev-nome">{{ aviso.severidade }}</span>
        <span class="na-inmet-sev-ajuda" v-if="aviso.ajuda">{{ aviso.ajuda }}</span>
      </div>
    </div>

    <!-- Período. Âmbar quando ainda não começou, como a estação muda no aside da Defesa
         Civil: o dado é válido, mas não descreve o agora. -->
    <div class="na-section na-inmet-periodo" :class="{ 'is-futuro': aviso.futuro }">
      <div class="na-inmet-periodo-topo" v-if="aviso.futuro">
        <font-awesome-icon icon="fa fa-clock" />
        <span>Ainda não começou</span>
      </div>

      <div class="na-inmet-periodo-linha">
        <div class="na-inmet-periodo-item">
          <span class="na-inmet-periodo-label">Início</span>
          <span class="na-inmet-periodo-valor">{{ formatar(aviso.inicio) }}</span>
        </div>
        <font-awesome-icon class="na-inmet-periodo-seta" icon="fa fa-arrow-right" />
        <div class="na-inmet-periodo-item">
          <span class="na-inmet-periodo-label">Fim</span>
          <span class="na-inmet-periodo-valor">{{ formatar(aviso.fim) }}</span>
        </div>
      </div>

      <div class="na-inmet-duracao" v-if="duracao">Duração: {{ duracao }}</div>
    </div>

    <template v-if="aviso.riscos && aviso.riscos.length">
      <div class="na-historico-header">
        <span class="na-historico-title">Riscos</span>
      </div>
      <div class="na-section">
        <p v-for="(risco, i) in aviso.riscos" :key="'r' + i" class="na-inmet-texto">
          {{ risco }}
        </p>
      </div>
    </template>

    <template v-if="aviso.instrucoes && aviso.instrucoes.length">
      <div class="na-historico-header">
        <span class="na-historico-title">O que fazer</span>
      </div>
      <div class="na-section">
        <div
          v-for="(instrucao, i) in aviso.instrucoes"
          :key="'i' + i"
          class="na-inmet-instrucao"
        >
          <font-awesome-icon class="na-inmet-instrucao-icone" icon="fa fa-circle-check" />
          <span>{{ instrucao }}</span>
        </div>
      </div>
    </template>

    <div class="na-historico-header">
      <span class="na-historico-title">Em caso de emergência</span>
    </div>
    <div class="na-section na-inmet-emergencia">
      <div class="na-inmet-fone">
        <span class="na-inmet-fone-num">199</span>
        <span class="na-inmet-fone-nome">Defesa Civil</span>
      </div>
      <div class="na-inmet-fone">
        <span class="na-inmet-fone-num">193</span>
        <span class="na-inmet-fone-nome">Corpo de Bombeiros</span>
      </div>
    </div>

    <!-- Fonte por último e sem card: é a procedência do dado, não o que se busca ao abrir
         o painel. Sigla e código bastam — um card com título e nome por extenso dava a esta
         linha o mesmo peso visual de Riscos e O que fazer. -->
    <p class="na-inmet-fonte">INMET · {{ aviso.idAviso }}</p>
  </div>
</template>

<script>
/**
 * Painel lateral de um aviso meteorológico do INMET.
 *
 * Só apresentação: recebe o aviso pronto por prop e não busca nada. A camada inteira é
 * servida de uma coleta em background no backend (ver InmetAvisosBackgroundService), e o
 * clique num polígono não pode virar tráfego para a origem — por isso não há aqui o
 * load()/carregar() que os outros asides do mapa têm.
 *
 * Os textos de `riscos` e `instrucoes` são redigidos pelo INMET e exibidos como vêm:
 * interpolação do Vue, nunca v-html — é conteúdo de terceiros.
 */
const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

export default {
  name: "NewAsideInmetAviso",
  props: {
    aviso: {
      type: Object,
      default: null,
    },
  },
  computed: {
    /**
     * Quanto tempo o aviso dura, em linguagem corrente ("15 horas", "2 dias e 11 horas").
     *
     * Complementa as duas datas: ler "08/09 08:47 → 08/09 23:59" e calcular de cabeça a
     * diferença é trabalho que a tela pode poupar.
     */
    duracao() {
      const inicio = this.paraData(this.aviso && this.aviso.inicio);
      const fim = this.paraData(this.aviso && this.aviso.fim);
      if (!inicio || !fim || fim <= inicio) return "";

      const minutos = Math.round((fim - inicio) / 60000);
      const dias = Math.floor(minutos / 1440);
      const horas = Math.floor((minutos % 1440) / 60);

      const partes = [];
      if (dias) partes.push(dias + (dias > 1 ? " dias" : " dia"));
      if (horas) partes.push(horas + (horas > 1 ? " horas" : " hora"));
      if (!partes.length) partes.push((minutos % 60) + " min");

      return partes.join(" e ");
    },
  },
  methods: {
    /**
     * "2026-09-08 08:47" -> "08/09 (ter) 08:47".
     *
     * O dia da semana entra porque a data sozinha não diz se o aviso é para hoje ou para
     * depois de amanhã, e é exatamente essa a pergunta de quem abre o painel.
     */
    formatar(texto) {
      const data = this.paraData(texto);
      if (!texto) return "--";
      if (!data) return texto;

      const dia = String(data.getDate()).padStart(2, "0");
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const hora = String(data.getHours()).padStart(2, "0");
      const min = String(data.getMinutes()).padStart(2, "0");

      return `${dia}/${mes} (${DIAS_SEMANA[data.getDay()]}) ${hora}:${min}`;
    },

    /**
     * Converte "yyyy-MM-dd HH:mm" (formato local que o backend repassa) em Date.
     *
     * Montado com componentes separados de propósito: `new Date("2026-09-08 08:47")` tem
     * suporte irregular entre navegadores, e a variante com "T" seria lida como UTC,
     * deslocando o horário em 3h no Brasil.
     */
    paraData(texto) {
      if (!texto) return null;

      const m = String(texto).match(
        /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/
      );
      if (!m) return null;

      return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    },
  },
};
</script>

<style scoped>
/* Tokens e classes espelham NewAsideDefesaCivil.vue / NewAsideExternalDevice.vue para os
   asides do mapa ficarem idênticos. */
.na-section {
  background: #fff; border-radius: 10px; padding: 12px; margin-bottom: 8px;
}

/* Título do fenômeno */
.na-inmet-titulo {
  font-size: 18px; font-weight: 700; color: #2f3b2a;
  margin: 0 0 10px; padding: 0 4px; line-height: 1.25;
}

/* Severidade: barra de cor + texto, sem preencher o card com a cor do INMET. */
.na-inmet-sev { display: flex; align-items: stretch; gap: 10px; padding: 10px 12px; }
.na-inmet-sev-barra { width: 4px; border-radius: 2px; flex-shrink: 0; }
.na-inmet-sev-texto { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.na-inmet-sev-nome { font-size: 13px; font-weight: 700; color: #333; }
.na-inmet-sev-ajuda { font-size: 12px; color: #6c757d; }

/* Período */
/* Aviso que ainda não começou fica âmbar, como a estação muda no aside da Defesa Civil. */
.na-inmet-periodo.is-futuro { background: #FFF6E5; border: 1px solid #F0D9A8; }
.na-inmet-periodo-topo {
  display: flex; align-items: center; gap: 6px; margin-bottom: 10px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
  color: #8a6d00;
}
.na-inmet-periodo-linha { display: flex; align-items: center; gap: 8px; }
.na-inmet-periodo-item {
  display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0;
}
.na-inmet-periodo-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: #9a9a9a;
}
.na-inmet-periodo-valor {
  font-size: 13px; font-weight: 600; color: #333; font-variant-numeric: tabular-nums;
}
.na-inmet-periodo-seta { color: #c4c4c4; font-size: 11px; flex-shrink: 0; }
.na-inmet-duracao {
  margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(0, 0, 0, .07);
  font-size: 11px; color: #6c757d;
}

/* Cabeçalhos de seção — mesmos tokens do na-historico-header dos outros asides. */
.na-historico-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; margin: 14px 0 8px;
}
.na-historico-title { font-size: 13px; font-weight: 600; color: #333; }

/* Riscos e instruções */
.na-inmet-texto { margin: 0; font-size: 13px; line-height: 1.5; color: #333; }
.na-inmet-texto + .na-inmet-texto { margin-top: 8px; }
.na-inmet-instrucao {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 13px; line-height: 1.5; color: #333;
}
.na-inmet-instrucao + .na-inmet-instrucao {
  margin-top: 8px; padding-top: 8px; border-top: 1px solid #F0F0F0;
}
.na-inmet-instrucao-icone { color: #006035; font-size: 12px; margin-top: 4px; flex-shrink: 0; }

/* Emergência: os telefones em destaque, porque é o que se procura com pressa. */
.na-inmet-emergencia { display: flex; gap: 8px; }
.na-inmet-fone {
  flex: 1; display: flex; flex-direction: column; gap: 2px;
  background: #F4F7F2; border: 1px solid #DDE5D8; border-radius: 8px; padding: 8px 10px;
}
.na-inmet-fone-num {
  font-size: 16px; font-weight: 700; color: #006035; font-variant-numeric: tabular-nums;
}
.na-inmet-fone-nome { font-size: 11px; color: #6c757d; }

/* Fonte: linha discreta, alinhada ao padding dos cabeçalhos de seção. */
.na-inmet-fonte {
  margin: 14px 0 0; padding: 0 4px;
  font-size: 12px; color: #6c757d; font-variant-numeric: tabular-nums;
}
</style>
